import React, { useEffect, useMemo, useState } from 'react';
import BarrasHorizontais from '../shared/BarrasHorizontais';
import ColunasMensais from '../shared/ColunasMensais';
import { MARK } from '../../../constants';
import { tresLivros } from '../../../utils/calculations/competencia';
import { periodoAnterior, comparacao } from '../../../utils/calculations/periodo';
import {
  composicaoDaReceita, receitaPorCliente, receitaPorModelo, receitaPorMes,
  ticketDoPeriodo,
} from '../../../utils/calculations/receita';
import {
  listarSaldoPorCliente, listarParcelasEmAberto, resumoDoSaldo, agendaDeRecebimento,
} from '../../../data/recebimentos';

/**
 * COMO O LABORATÓRIO VAI, E DE ONDE VEM CADA REAL.
 *
 * A primeira versão disto respondia duas perguntas — quanto se realizou e quanto
 * virou fatura — e parava aí. "Realizado: R$ 47.320" não deixa ninguém decidir
 * nada: não diz se veio de dois clientes ou de vinte, se é embrião transferido ou
 * quilometragem repassada, nem se o mês foi melhor que o anterior.
 *
 * Agora o mesmo número é aberto por quatro eixos, e os quatro somam o mesmo
 * total. Essa é a regra que segura o painel inteiro: cartão que soma diferente
 * do cartão vizinho faz quem lê parar de confiar nos dois.
 *
 * DUAS NATUREZAS, e a separação entre elas é a coisa mais importante da tela:
 *
 *   NO PERÍODO   competência. O trabalho que FECHOU entre as datas, tenha virado
 *                documento ou não. É o que se compara com o período anterior.
 *   HOJE         retrato do caixa. Quanto o cliente deve neste instante e quando
 *                entra. Não tem período: saldo de ontem não é saldo.
 *
 * Misturar as duas era o que fazia "faturado" nunca bater com "faturado".
 */

const emReais = (v) => Number(v || 0).toLocaleString('pt-BR',
  { style: 'currency', currency: 'BRL' });

const emReaisCurto = (v) => Number(v || 0).toLocaleString('pt-BR',
  { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const pct = (v) => `${Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

/** Um cartão de número. `forte` é o que a pessoa veio ver. */
const Kpi = ({ rotulo, valor, apoio, forte = false, alerta = false, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-3.5">
    <p className="text-xs text-slate-500">{rotulo}</p>
    <p className={`tabular-nums text-slate-900 leading-tight ${forte
      ? 'text-2xl font-semibold' : 'text-lg font-semibold'}`}>
      {emReais(valor)}{alerta && Number(valor) > 0 ? ` ${MARK.WARN}` : ''}
    </p>
    {apoio && <p className="text-xs text-slate-400 mt-0.5">{apoio}</p>}
    {children}
  </div>
);

/** Um bloco do painel. Título, uma linha dizendo o que ele responde, e o gráfico. */
const Cartao = ({ titulo, pergunta, children, className = '' }) => (
  <div className={`bg-white rounded-xl border border-slate-200 p-4 ${className}`}>
    <p className="text-sm font-semibold text-slate-900">{titulo}</p>
    {pergunta && <p className="text-xs text-slate-500 mb-3">{pergunta}</p>}
    {children}
  </div>
);

const PainelFinanceiro = ({
  laboratoryId,
  services = [],
  calculateServiceValue,
  // As datas vêm da barra de filtros do relatório, a mesma dos outros painéis:
  // um relatório com dois recortes de tempo na tela é um relatório que mente
  // para quem lê o segundo número achando que é do primeiro.
  de = null,
  ate = null,
}) => {
  const [linhas, setLinhas] = useState([]);
  const [emAberto, setEmAberto] = useState([]);

  useEffect(() => {
    if (!laboratoryId) return undefined;
    let cancelado = false;
    Promise.all([
      listarSaldoPorCliente(laboratoryId),
      listarParcelasEmAberto(laboratoryId),
    ])
      .then(([saldo, parcelas]) => {
        if (cancelado) return;
        setLinhas(saldo || []);
        setEmAberto(parcelas || []);
      })
      .catch(e => console.error('saldo por cliente:', e));
    return () => { cancelado = true; };
  }, [laboratoryId]);

  const recorte = useMemo(() => ({ de, ate }), [de, ate]);
  const anterior = useMemo(() => periodoAnterior(recorte), [recorte]);

  const livros = useMemo(
    () => tresLivros(services, { ...recorte, valorDoServico: calculateServiceValue }),
    [services, recorte, calculateServiceValue]);

  const livrosAntes = useMemo(
    () => tresLivros(services, { ...anterior, valorDoServico: calculateServiceValue }),
    [services, anterior, calculateServiceValue]);

  // Só compara quando há recorte: sem data inicial não existe período anterior,
  // e mostrar uma queda de cem por cento ali seria inventar informação.
  const vsAntes = recorte.de ? comparacao(livros.realizado, livrosAntes.realizado) : null;

  const composicao = useMemo(() => composicaoDaReceita(services, recorte), [services, recorte]);
  const porCliente = useMemo(() => receitaPorCliente(services, recorte), [services, recorte]);
  const porModelo  = useMemo(() => receitaPorModelo(services, recorte), [services, recorte]);
  const porMes     = useMemo(() => receitaPorMes(services, recorte), [services, recorte]);
  const ticket     = useMemo(() => ticketDoPeriodo(services, recorte), [services, recorte]);

  const t = resumoDoSaldo(linhas);
  const agenda = agendaDeRecebimento(emAberto);

  const periodoEscrito = recorte.de && recorte.ate
    ? `${new Date(`${recorte.de}T00:00:00`).toLocaleDateString('pt-BR')} a `
      + `${new Date(`${recorte.ate}T00:00:00`).toLocaleDateString('pt-BR')}`
    : 'todo o histórico';

  // Barra e tooltip: a barra mostra o número curto, e o detalhe escreve o valor
  // cheio com a fatia — que é o que responde "quanto disso é isso".
  const paraBarra = (linhas_) => linhas_.map(l => ({ ...l, valor: Math.round(l.valor) }));
  const detalhe = (d) => `${d.rotulo}: ${emReais(d.valor)}${d.pct != null ? ` · ${pct(d.pct)}` : ''}`;

  return (
    <div className="space-y-4">
      {/* ── NO PERÍODO: os números que se comparam ───────────────────────── */}
      <div>
        <p className="text-xs text-slate-500 mb-2">No período · {periodoEscrito}</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi
            rotulo="Realizado" valor={livros.realizado} forte
            apoio={`${livros.servicos} serviço(s) com trabalho fechado`}
          >
            {/* A comparação com o período ANTERIOR DE MESMO TAMANHO. Sem base
                não inventa porcentagem: no primeiro mês de uso ela escreve que
                não havia com o que comparar. */}
            {vsAntes && !vsAntes.igual && (
              <p className="text-xs text-slate-600 mt-1">
                {vsAntes.subiu ? '▲' : '▼'} {emReaisCurto(Math.abs(vsAntes.delta))}
                {vsAntes.pct !== null
                  ? ` (${vsAntes.pct > 0 ? '+' : ''}${vsAntes.pct}%) vs anterior`
                  : ' — sem período anterior com movimento'}
              </p>
            )}
          </Kpi>

          <Kpi rotulo="Faturado" valor={livros.faturado} apoio="já tem documento emitido" />
          <Kpi
            rotulo="A faturar" valor={livros.aFaturar} alerta
            apoio={`${livros.naoFaturados.length} pronto(s), sem fatura`}
          />
          <Kpi
            rotulo="Ticket médio" valor={ticket.media}
            apoio={`mediana ${emReaisCurto(ticket.mediana)} · maior ${emReaisCurto(ticket.maior)}`}
          >
            {/* Média sozinha engana quando um serviço grande divide a lista com
                nove pequenos. A concentração diz se o mês depende de poucos. */}
            {ticket.servicos >= 3 && (
              <p className="text-xs text-slate-600 mt-1">
                {pct(ticket.concentracao)} do total nos três maiores
              </p>
            )}
          </Kpi>
        </div>
      </div>

      {/* ── MÊS A MÊS ─────────────────────────────────────────────────────── */}
      <Cartao titulo="Mês a mês" pergunta="Está melhorando? A linha tracejada é a média do período.">
        <ColunasMensais dados={porMes} />
      </Cartao>

      {/* ── DE ONDE VEM ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Cartao
          titulo="De onde vem cada real"
          pergunta="O que dentro do serviço gerou a receita: o preço do modelo, o congelamento e cada repasse."
        >
          <BarrasHorizontais
            dados={paraBarra(composicao)} larguraRotulo="w-40" detalhe={detalhe}
          />
        </Cartao>

        <Cartao titulo="Por cliente" pergunta="De quem veio. Os dez maiores; o resto soma em “outros”.">
          <BarrasHorizontais
            dados={paraBarra(porCliente)} larguraRotulo="w-40"
            detalhe={(d) => `${detalhe(d)} · ${d.servicos} serviço(s)`}
          />
        </Cartao>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Cartao
          titulo="Por modelo de cobrança"
          pergunta="Por qual regra foi cobrado — diz de que o laboratório vive."
        >
          <BarrasHorizontais
            dados={paraBarra(porModelo)} larguraRotulo="w-40"
            detalhe={(d) => `${detalhe(d)} · ${d.servicos} serviço(s)`}
          />
        </Cartao>

        {/* ── HOJE: o retrato do caixa ─────────────────────────────────────
            Não depende do período, e por isso mora num cartão à parte, com o
            rótulo dizendo isso. Saldo de ontem não é saldo. */}
        <Cartao titulo="Hoje" pergunta="O caixa neste instante. Não depende do período escolhido.">
          <div className="grid grid-cols-2 gap-3 mb-3">
            {/* "Faturado ao cliente", e não só "Faturado".
                Este número soma as DUAS cobranças, serviço e entrada; o livro
                acima conta o preço do cadastro dos serviços que já têm fatura.
                Os dois estavam certos e apareciam na mesma sessão sem nada
                dizendo qual era qual. A quebra embaixo é o que os reconcilia. */}
            <div>
              <p className="text-xs text-slate-500">Faturado ao cliente</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900">{emReais(t.faturado)}</p>
              {t.faturadoEntrada > 0 && (
                <p className="text-xs text-slate-400">
                  {emReaisCurto(t.faturadoServico)} de serviço + {emReaisCurto(t.faturadoEntrada)} de entrada
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-slate-500">Recebido</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900">{emReais(t.recebido)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">A receber</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900">{emReais(t.saldo)}</p>
              <p className="text-xs text-slate-400">{t.clientes} cliente(s)</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Vencido</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900">
                {emReais(agenda.vencido)}{agenda.vencido > 0 ? ` ${MARK.WARN}` : ''}
              </p>
              <p className="text-xs text-slate-400">por parcela, não por fatura</p>
            </div>
          </div>

          {/* ── QUANDO O DINHEIRO ENTRA ──────────────────────────────────
              Em barra e não em linha de texto: a pergunta aqui é de PROPORÇÃO —
              o quanto do que falta receber já venceu, e o quanto ainda está
              longe. Quatro números soltos obrigam a comparar de cabeça. */}
          {agenda.total > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs text-slate-500 mb-2">
                Quando entra · {emReais(agenda.total)} em aberto
              </p>
              <BarrasHorizontais
                larguraRotulo="w-28"
                dados={[
                  ['Vencido', agenda.vencido],
                  ['Até 7 dias', agenda.ate7],
                  ['Até 30 dias', agenda.ate30],
                  ['Depois', agenda.depois],
                  ['Sem vencimento', agenda.semData],
                ].filter(([, v]) => v > 0)
                  .map(([rotulo, valor]) => ({ rotulo, valor: Math.round(valor) }))}
                detalhe={(d) => `${d.rotulo}: ${emReais(d.valor)}`}
              />
              {/* Parcela sem vencimento não vence nunca: ela sumia de toda
                  cobrança em vez de aparecer no fim da fila. */}
              {agenda.semData > 0 && (
                <p className="text-xs text-slate-700 mt-2">
                  {MARK.WARN} {emReais(agenda.semData)} em parcela sem vencimento. Fatura sem data
                  não entra em cobrança nenhuma.
                </p>
              )}
            </div>
          )}
        </Cartao>
      </div>

      {/* O QUE ESTÁ PRONTO E NÃO FOI FATURADO, com nome.
          A cifra sozinha não dá o que fazer: "R$ 4.200 a faturar" manda a
          pessoa procurar quais são. */}
      {livros.naoFaturados.length > 0 && (
        <Cartao
          titulo={`${MARK.WARN} Pronto e sem fatura`}
          pergunta="Trabalho fechado no período que ainda não virou documento."
        >
          <div className="divide-y divide-slate-100 -mx-1">
            {livros.naoFaturados.map(service => (
              <div key={service.id} className="px-1 py-1.5 flex items-baseline justify-between gap-3">
                <span className="text-sm text-slate-800 truncate">
                  {service.client_name || 'Sem cliente'}
                  {service.service_code && (
                    <span className="text-xs font-mono text-slate-400"> #{service.service_code}</span>
                  )}
                </span>
                <span className="text-sm font-semibold text-slate-900 tabular-nums shrink-0">
                  {emReais(calculateServiceValue ? calculateServiceValue(service) : 0)}
                </span>
              </div>
            ))}
          </div>
        </Cartao>
      )}
    </div>
  );
};

export default PainelFinanceiro;
